import { USER_ROLES } from './index';
import { PORTAL_ACCENT } from '../styles/portalTheme';

/** Backend `data.type` values for portal event notifications. */
export const EVENT_NOTIFICATION_TYPE = {
  PARTNER_EVENT_SUBMITTED: 'partner_event_submitted',
  PARTNER_EVENT_RESUBMITTED: 'partner_event_resubmitted',
  PARTNER_EVENT_CHANGES_REQUESTED: 'partner_event_changes_requested',
  PARTNER_EVENT_APPROVED: 'partner_event_approved',
  PARTNER_EVENT_PUBLISHED: 'partner_event_published',
  EVENT_FEEDBACK_SUBMITTED: 'event_feedback_submitted',
};

export const EVENT_DETAILS_TAB_QUERY = {
  FEEDBACK: 'feedback',
};

export const EVENT_FEEDBACK_SUBMITTED_DEFAULT_MESSAGE =
  'A new event review was submitted and is pending moderation';

/**
 * Portal event notification catalog (admin vs partner).
 * @see backend event notification emitters
 */
export const EVENT_NOTIFICATION_CATALOG = {
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_SUBMITTED]: {
    recipientRole: USER_ROLES.ADMIN,
    actorRole: USER_ROLES.PARTNER,
    label: 'Event submitted for review',
    accentColor: PORTAL_ACCENT,
  },
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_RESUBMITTED]: {
    recipientRole: USER_ROLES.ADMIN,
    actorRole: USER_ROLES.PARTNER,
    label: 'Event resubmitted for review',
    accentColor: PORTAL_ACCENT,
  },
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_CHANGES_REQUESTED]: {
    recipientRole: USER_ROLES.PARTNER,
    actorRole: null,
    label: 'Changes requested',
    accentColor: '#1565c0',
  },
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_APPROVED]: {
    recipientRole: USER_ROLES.PARTNER,
    actorRole: USER_ROLES.ADMIN,
    label: 'Event approved',
    accentColor: '#2e7d32',
  },
  [EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_PUBLISHED]: {
    recipientRole: USER_ROLES.PARTNER,
    actorRole: null,
    label: 'Event published',
    accentColor: '#6a1b9a',
  },
  [EVENT_NOTIFICATION_TYPE.EVENT_FEEDBACK_SUBMITTED]: {
    recipientRoles: [USER_ROLES.ADMIN, USER_ROLES.PARTNER],
    actorRole: null,
    label: 'Event review submitted',
    accentColor: PORTAL_ACCENT,
    defaultMessage: EVENT_FEEDBACK_SUBMITTED_DEFAULT_MESSAGE,
  },
};

const normalizeTypeKey = (type) =>
  String(type || '')
    .trim()
    .toLowerCase();

export const getEventNotificationMeta = (type) => {
  const key = normalizeTypeKey(type);
  return EVENT_NOTIFICATION_CATALOG[key] ?? null;
};

export const isEventNotificationForRole = (type, userRole) => {
  const meta = getEventNotificationMeta(type);
  if (!meta) return false;
  if (Array.isArray(meta.recipientRoles) && meta.recipientRoles.length > 0) {
    return meta.recipientRoles.includes(userRole);
  }
  return meta.recipientRole === userRole;
};

const buildEventFeedbackModerationPath = (eventId, isPartner) => {
  const base = isPartner ? `/partner-events/${eventId}` : `/events/${eventId}`;
  return `${base}?tab=${EVENT_DETAILS_TAB_QUERY.FEEDBACK}`;
};

/**
 * Deep link for push click / inbox row.
 * @param {object} params
 * @param {string} [params.type] - notification type
 * @param {string} [params.eventId]
 * @param {string} params.viewerRole - USER_ROLES.ADMIN | USER_ROLES.PARTNER
 */
export const resolveEventNotificationPath = ({ type, eventId, viewerRole }) => {
  const id = eventId ? String(eventId) : null;
  const isPartner = viewerRole === USER_ROLES.PARTNER;
  const normalizedType = normalizeTypeKey(type);

  if (!id) {
    return isPartner ? '/partner-notifications' : '/notifications';
  }

  switch (normalizedType) {
    case EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_SUBMITTED:
    case EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_RESUBMITTED:
      return `/events/${id}?mode=review`;

    case EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_CHANGES_REQUESTED:
      return `/partner-events/${id}/moderation-feedback`;

    case EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_APPROVED:
    case EVENT_NOTIFICATION_TYPE.PARTNER_EVENT_PUBLISHED:
      return `/partner-events/${id}`;

    case EVENT_NOTIFICATION_TYPE.EVENT_FEEDBACK_SUBMITTED:
      return buildEventFeedbackModerationPath(id, isPartner);

    default:
      return isPartner ? `/partner-events/${id}` : `/events/${id}`;
  }
};
